import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ProductionEntry } from '../../../dashboard/production.model';
import { ProductionEntryInsight } from '../../../production-inventory/production-inventory.model';

@Component({
  selector: 'app-line-chart',
  standalone: false,
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
})
export class LineChartComponent implements OnChanges, OnInit, OnDestroy {
  @Input() productionEntries: ProductionEntryInsight[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productionEntries']) {
      this._initLineChart();
    }
  }

  ngOnInit(): void {
    this._initLineChart();
  }

  private _root: am5.Root | null = null;

  private _initLineChart(): void {
    if (this.productionEntries?.length === 0) {
      return;
    }

    if (this._root) {
      this._root?.dispose();
    }

    let root = am5.Root.new('chartdiv');

    // Chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
      })
    );

    const data = this._processChartData();

    // X Axis
    let xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0,
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {}),
        tooltipDateFormat: 'MMM dd',
      })
    );
    xAxis.get('renderer').labels.template.setAll({
      fill: am5.color(0x6c757d),
    });

    // Y Axis
    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0,
        extraMax: 0.1,
      })
    );
    yAxis.get('renderer').labels.template.setAll({
      fill: am5.color(0x6c757d),
    });

    // Hide Y-axis grid lines
    xAxis.get('renderer').grid.template.setAll({
      visible: false,
    });

    // Series
    let series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Production',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        stroke: am5.color(0x4880ff),
      })
    );

    // Create gradient fill for the area
    series.fills.template.setAll({
      fillGradient: am5.LinearGradient.new(root, {
        rotation: 90,
        stops: [
          { color: am5.color(0x4880ff), opacity: 0.4 },
          { color: am5.color(0x4880ff), opacity: 0 },
        ],
      }),
      visible: true,
    });

    series.bullets.push(root => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: am5.color(0x4880ff),
        stroke: am5.color(0xffffff),
        strokeWidth: 2,
        tooltipText: `[bold]{date.formatDate('dd MMM yyyy')}[/]\nTotal Production: {value} NOS\n─────────────────\n{tooltipContent}`,
      });

      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    series.data.setAll(data);

    // Cleanup reference
    this._root = root;
    root._logo?.dispose();
  }

  private _processChartData() {
    // Group entries by date, aggregating totals
    const grouped = new Map<
      number,
      {
        value: number;
        entries: Array<{
          productName: string;
          employee: string;
          warehouse: string;
          box_produced: string;
          packaging_type: string;
          value: number;
        }>;
      }
    >();

    for (const entry of this.productionEntries) {
      const dateKey = new Date(entry.production_date).setHours(0, 0, 0, 0);
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, { value: 0, entries: [] });
      }
      const group = grouped.get(dateKey)!;
      group.value += entry.total_nos_produced;
      group.entries.push({
        productName: entry.product_name,
        employee: entry.created_by_name,
        warehouse: entry.warehouse_name,
        box_produced: entry.box_produced,
        packaging_type: entry.packaging_type,
        value: entry.total_nos_produced,
      });
    }

    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([date, group]) => ({
        date,
        value: group.value,
        tooltipContent: group.entries
          .map(
            e =>
              `[bold]${e.productName}[/] — ${e.value} NOS (${e.packaging_type})\nBoxes: ${e.box_produced}\nBy: ${e.employee}`
          )
          .join('\n─────────────────\n'),
      }));
  }

  ngOnDestroy(): void {
    this._root?.dispose();
  }
}
